import React, { PropsWithChildren, useContext } from 'react';
import { WidgetType } from '../widgets/WidgetType';
import { PubSub } from '@instinet-analytics/core-lib';
import { AnyConfig } from '../widgets/WidgetRegistry';
import { WidgetId } from '../widgets/WidgetId';

export type AddLayoutDragSourcePubSubItem = {
    element: HTMLDivElement;
    type: WidgetType;
    title: string;
    config?: AnyConfig;
    uid: string;
};

/*
 * Manages drag sources for Golden Layout
 */
export class AddLayoutDragSourcePubSub {
    private pubSub = new PubSub<AddLayoutDragSourcePubSubItem>();
    private draggingPubSub = new PubSub<AddLayoutDragSourcePubSubItem>();
    private draggedPubSub = new PubSub<WidgetId>();
    private elementsAdded: HTMLDivElement[] = [];

    /*
     * Add an element as a drag source for the layout - for example from the Add Widgets modal
     */
    public publishAdd(item: AddLayoutDragSourcePubSubItem) {
        if (this.elementsAdded.indexOf(item.element) > -1) {
            // Already have created a drag source for this element
            return;
        }
        this.elementsAdded.push(item.element);
        this.pubSub.publish(item);
    }

    /*
     * Subscribes for new drag sources, for golden layout to create the actual drag sources
     */
    public subscribeAdd(
        id: string,
        cb: (item?: AddLayoutDragSourcePubSubItem) => void
    ) {
        this.pubSub.subscribe(id, cb);
    }

    /*
     * Publish the dragging started event
     */
    public publishDragging(item: AddLayoutDragSourcePubSubItem) {
        this.draggingPubSub.publish(item);
    }

    public publishDragged(id: WidgetId) {
        this.draggedPubSub.publish(id);
    }

    /*
     * Listen to the dragging started event, to allow the AddWidgetModal to close itself
     */
    public subscribeDragging(
        id: string,
        cb: (item?: AddLayoutDragSourcePubSubItem) => void
    ) {
        this.draggingPubSub.subscribe(id, cb);
    }

    public subscribeDragged(id: string, cb: (id?: WidgetId) => void) {
        this.draggedPubSub.subscribe(id, cb);
    }

    public unsubscribeDragging(id: string) {
        this.draggingPubSub.unsubscribe(id);
    }

    public unsubscribeDragged(id: string) {
        this.draggedPubSub.unsubscribe(id);
    }
}

export interface AddLayoutDragSourceContextType {
    addLayoutDragSourcePubSub: AddLayoutDragSourcePubSub;
}

const defaultValue: AddLayoutDragSourceContextType = {
    addLayoutDragSourcePubSub: new AddLayoutDragSourcePubSub(),
};

const AddLayoutDragSourceContext =
    React.createContext<AddLayoutDragSourceContextType>(defaultValue);

export function AddLayoutDragSourceContextProvider(props: PropsWithChildren) {
    return (
        <AddLayoutDragSourceContext.Provider value={defaultValue}>
            {props.children}
        </AddLayoutDragSourceContext.Provider>
    );
}

export const useAddLayoutDragSourcePubSub = () =>
    useContext(AddLayoutDragSourceContext).addLayoutDragSourcePubSub;
